/**
 * Confirm a cross-space move. Under the hood it is a transfer (fresh docs in
 * the target space, original trashed after the server acknowledges), so the
 * user gets one honest sentence about what changes before it runs.
 */
import { useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { ArrowRightLeft, X } from "lucide-react";
import { create } from "zustand";
import { ctxFor, resolveItem } from "@/data/store";
import { useSharedFolders } from "@/data/sharedFolders";
import { transferItem } from "@/data/transfer";
import { openItem } from "@/commands/nav";
import { Button } from "@/ui/Button";
import { IconButton } from "@/ui/IconButton";
import { toast } from "@/ui/toast";
import s from "./SignInDialog.module.css";

export interface TransferRequest {
  itemId: string;
  sourceSpaceId: string;
  targetSpaceId: string;
  targetParentId: string | null;
}

interface TransferDialogStore {
  request: TransferRequest | null;
  open: (request: TransferRequest) => void;
  close: () => void;
}
export const useTransferDialog = create<TransferDialogStore>((set) => ({
  request: null,
  open: (request) => set({ request }),
  close: () => set({ request: null }),
}));

export function TransferConfirmDialog() {
  const { request, close } = useTransferDialog();
  const folders = useSharedFolders((st) => st.folders);
  const [busy, setBusy] = useState(false);

  if (!request) {
    return null;
  }

  const res = resolveItem(request.itemId);
  const spaceName = (spaceId: string) =>
    folders.find((f) => f.id === spaceId)?.title ?? "your personal notes";
  const targetShared = folders.some((f) => f.id === request.targetSpaceId);

  const run = async () => {
    if (busy) return;
    const sourceCtx = ctxFor(request.sourceSpaceId);
    const targetCtx = ctxFor(request.targetSpaceId);
    if (!sourceCtx || !targetCtx || !res) {
      toast.error("That folder is not available right now.");
      close();
      return;
    }
    setBusy(true);
    try {
      const moved = await transferItem({
        sourceCtx,
        targetCtx,
        rootId: request.itemId,
        targetParentId: request.targetParentId,
      });
      if (moved) {
        toast.success(`Moved to ${spaceName(request.targetSpaceId)}.`);
        close();
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog.Root open onOpenChange={(o) => !o && close()}>
      <Dialog.Portal>
        <Dialog.Overlay className={s.overlay} />
        <Dialog.Content className={s.dialog}>
          <div className={s.header}>
            <ArrowRightLeft size={20} />
            <Dialog.Title className={s.title}>Move "{res?.item.title || "Untitled"}"?</Dialog.Title>
          </div>
          <Dialog.Description className={s.subtitle}>
            This moves it from {spaceName(request.sourceSpaceId)} to {spaceName(request.targetSpaceId)}
            {res?.item.type === "folder" ? ", including everything inside" : ""}.
            {targetShared
              ? " Everyone in that folder will see it, and it will count against every member's storage."
              : " It becomes private to you."}{" "}
            The original goes to the source trash for 30 days.
          </Dialog.Description>
          <div className={s.form}>
            <Button variant="solid" disabled={busy} onClick={() => void run()}>
              {busy ? "Moving..." : "Move it"}
            </Button>
            <Button variant="ghost" onClick={close}>
              Cancel
            </Button>
          </div>
          <Dialog.Close asChild>
            <IconButton label="Close" className={s.close}>
              <X size={14} />
            </IconButton>
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
